import {
    Utils, Config, JsonGroup, Field, JsonLogicTree,
    JsonTree,
    JsonLogicResult
} from '@react-awesome-query-builder/core';

import transformJsonDataQueryToGenerateStateOptimized from './transformJsonDataQueryToGenerateStateOptimized';
import convertToImmutableTreeComplete from './convertToImmutableTreeComplete';

interface CaseItem {
    whenSql?: string;
    thenSql?: string;
    whenJsonLogic?: JsonLogicResult;
    thenJsonLogic?: JsonLogicResult;
    when: JsonGroup;
    thenQuery?: JsonGroup;
    thenType?: "QUERY" | "VALUE" | "FIELD";
    thenValue?: any;
}

const QueryBuilderCustomUtils = {
    // SQL Utilities
    formatSql(sql: string): string {
        const finalSql = sql?.replaceAll("A8_NO_OPERATOR", "") || "";
        return finalSql;
    },

    sqlFormatter(whenData: { caseList: CaseItem[]; elseSql: string; }): string {
        let sqlStr = "CASE";
        sqlStr += whenData.caseList
            .map(
                (caseItem) =>
                    `\n\tWHEN ${caseItem.whenSql || ""} \n\t\t THEN ${caseItem.thenSql}`
            )
            .join("\n");
        sqlStr += `\n\n\tELSE ${whenData.elseSql} \nEND`;
        return sqlStr;
    },

    getSql(jsonData: {
        elseValue: string;
        elseQuery: JsonTree;
        elseType: any; caseList?: CaseItem[]; elseSql?: string; query?: JsonGroup,
    }, config: Config): string {
        if (jsonData.caseList) {
            jsonData.caseList.forEach((caseItem) => {
                const whenState = convertToImmutableTreeComplete(transformJsonDataQueryToGenerateStateOptimized(caseItem.when));
                caseItem.whenSql = this.formatSql(
                    Utils.sqlFormat(whenState, config)
                );
                if (caseItem.thenQuery) {
                    switch (caseItem.thenType) {
                        case "QUERY":
                            const thenState = convertToImmutableTreeComplete(transformJsonDataQueryToGenerateStateOptimized(caseItem.thenQuery));
                            caseItem.thenSql = this.formatSql(
                                Utils.sqlFormat(thenState, config)
                            );
                            break;
                        case "VALUE":
                            caseItem.thenSql = caseItem.thenValue;
                            break;
                        case "FIELD":
                            caseItem.thenSql = caseItem.thenValue;
                            break;
                    }
                }
            });
            switch (jsonData.elseType) {
                case "QUERY":
                    const elseState = convertToImmutableTreeComplete(transformJsonDataQueryToGenerateStateOptimized(jsonData.elseQuery));
                    jsonData.elseSql = this.formatSql(
                        Utils.sqlFormat(elseState, config)
                    );
                    break;
                case "VALUE":
                    jsonData.elseSql = jsonData.elseValue;
                    break;
                case "FIELD":
                    jsonData.elseSql = jsonData.elseValue;
                    break;
            }
            return this.sqlFormatter(jsonData);
        } else {
            const queryState = convertToImmutableTreeComplete(transformJsonDataQueryToGenerateStateOptimized(jsonData.query));
            return this.formatSql(
                Utils.sqlFormat(queryState, config)
            );
        }
    },

    // JSON Logic Utilities
    jsonLogicRemoveUnwanted(jsonLogicObj: any): any {
        // Handle null/undefined
        if (jsonLogicObj === null || jsonLogicObj === undefined) {
            return jsonLogicObj;
        }

        // Handle non-object types (primitives)
        if (typeof jsonLogicObj !== 'object') {
            return jsonLogicObj;
        }

        // Handle arrays
        if (Array.isArray(jsonLogicObj)) {
            return jsonLogicObj.map((item) => 
                QueryBuilderCustomUtils.jsonLogicRemoveUnwanted(item)
            );
        }

        // Handle objects with 'and' property
        if (jsonLogicObj.and && Array.isArray(jsonLogicObj.and)) {
            if (jsonLogicObj.and.length === 1) {
                return QueryBuilderCustomUtils.jsonLogicRemoveUnwanted(jsonLogicObj.and[0]);
            }
        }

        // Handle plain objects
        const result: any = {};
        Object.keys(jsonLogicObj).forEach((key) => {
            result[key] = QueryBuilderCustomUtils.jsonLogicRemoveUnwanted(
                jsonLogicObj[key]
            );
        });
        return result;
    },

    removeSingleArr(jsonLogicObj: any): any {
        // Handle null/undefined
        if (jsonLogicObj === null || jsonLogicObj === undefined) {
            return jsonLogicObj;
        }

        // Handle non-object types (primitives like strings, numbers, booleans)
        if (typeof jsonLogicObj !== 'object') {
            return jsonLogicObj;
        }

        // Handle arrays
        if (Array.isArray(jsonLogicObj)) {
            // If array has only one element, extract it
            if (jsonLogicObj.length === 1) {
                return QueryBuilderCustomUtils.removeSingleArr(jsonLogicObj[0]);
            }
            // Otherwise process each element
            return jsonLogicObj.map((item) => 
                QueryBuilderCustomUtils.removeSingleArr(item)
            );
        }

        // Handle plain objects - now it's safe to use Object.keys
        const result: any = {};
        Object.keys(jsonLogicObj).forEach((key) => {
            result[key] = QueryBuilderCustomUtils.removeSingleArr(
                jsonLogicObj[key]
            );
        });
        return result;
    },

    // Rewritten jsonLogicFormatter to produce flat "if" array for CASE/WHEN/ELSE, supporting multiple cases
    jsonLogicFormatter(whenData: any): any {
        if (!whenData || !whenData.caseList) {
            return null;
        }
        // Build a flat array for the "if" key: [cond1, then1, cond2, then2, ..., else]
        const ifArr: any[] = [];
        for (let index = 0; index < whenData.caseList.length; index++) {
            const caseItem = whenData.caseList[index];
            if (!caseItem || !caseItem.whenJsonLogic || !caseItem.thenJsonLogic) {
                continue;
            }
            if (caseItem.whenJsonLogic?.errors?.length) {
                throw caseItem.whenJsonLogic.errors;
            }
            if (caseItem.thenJsonLogic?.errors?.length) {
                throw caseItem.thenJsonLogic.errors;
            }
            // Remove unnecessary "and" wrapper if present and only one condition
            let whenLogic = caseItem.whenJsonLogic.logic;
            if (
                whenLogic &&
                typeof whenLogic === "object" &&
                whenLogic.and &&
                Array.isArray(whenLogic.and) &&
                whenLogic.and.length === 1
            ) {
                whenLogic = whenLogic.and[0];
            }
            ifArr.push(whenLogic, caseItem.thenJsonLogic.logic);
        }
        // Add else at the end
        if (whenData.elseJsonLogic && whenData.elseJsonLogic.errors?.length) {
            throw whenData.elseJsonLogic.errors;
        }
        if (whenData.elseJsonLogic) {
            ifArr.push(whenData.elseJsonLogic.logic);
        }
        return { if: ifArr };
    },

    getJsonLogic(jsonData: any, config: Config): JsonLogicTree {
        let jsonLogicObj: any = {};
        if (jsonData.caseList) {
            jsonData.caseList.forEach((caseItem) => {
                caseItem.whenJsonLogic = Utils.jsonLogicFormat(
                    convertToImmutableTreeComplete(transformJsonDataQueryToGenerateStateOptimized(caseItem.when)),
                    config
                );

                if (caseItem.thenQuery) {
                    switch (caseItem.thenType) {
                        case "QUERY":
                            caseItem.thenJsonLogic = Utils.jsonLogicFormat(
                                convertToImmutableTreeComplete(transformJsonDataQueryToGenerateStateOptimized(caseItem.thenQuery)),
                                config
                            );
                            break;
                        case "VALUE":
                            caseItem.thenJsonLogic = { logic: caseItem.thenValue };
                            break;
                        case "FIELD":
                            caseItem.thenJsonLogic = { logic: { var: caseItem.thenValue } };
                            break;

                        default:
                            break;
                    }
                } else if (caseItem.thenType === "VALUE") {
                    caseItem.thenJsonLogic = { logic: caseItem.thenValue };
                } else if (caseItem.thenType === "FIELD") {
                    caseItem.thenJsonLogic = { logic: { var: caseItem.thenValue } };
                }
            });
            switch (jsonData.elseType) {
                case "QUERY":
                    jsonData.elseJsonLogic = Utils.jsonLogicFormat(
                        convertToImmutableTreeComplete(transformJsonDataQueryToGenerateStateOptimized(jsonData.elseQuery)),
                        config
                    );
                    break;
                case "VALUE":
                    jsonData.elseJsonLogic = { logic: jsonData.elseValue };
                    break;
                case "FIELD":
                    jsonData.elseJsonLogic = { logic: { var: jsonData.elseValue } };
                    break;
                default:
                    break;
            }
            jsonLogicObj = QueryBuilderCustomUtils.jsonLogicFormatter(jsonData);
        } else {
            const loadStart = performance.now();

            // First transform to add paths
            const plainState = transformJsonDataQueryToGenerateStateOptimized(jsonData.query);
            console.log('Plain state with paths:', JSON.stringify(plainState, null, 2));

            // Then convert to proper Immutable structure
            const state = convertToImmutableTreeComplete(plainState);
            console.log('Generated immutable state:', state);

            const loadEnd = performance.now();
            console.log(`State creation time: ${(loadEnd - loadStart).toFixed(2)}ms`);

            const jsonLogicStart = performance.now();
            const { logic } = Utils.jsonLogicFormat(state, config);
            const jsonLogicEnd = performance.now();
            console.log(`JSON Logic generation time: ${(jsonLogicEnd - jsonLogicStart).toFixed(2)}ms`);
            jsonLogicObj = logic;
            return jsonLogicObj;
        }
        
        // Clean up the JSON logic object
        jsonLogicObj = QueryBuilderCustomUtils.jsonLogicRemoveUnwanted(jsonLogicObj);
        jsonLogicObj = QueryBuilderCustomUtils.removeSingleArr(jsonLogicObj);
        return jsonLogicObj;
    },

    // Other Utilities
    createFields(columns: any[]): any {
        const finalFields = {};
        columns.forEach((column) => {
            const finalColumnObj: any = {
                type: "",
                label: column.name || "",
                valueSources: ["value"],
            };
            switch (column.dataType) {
                case "string":
                    finalColumnObj.type = "text";
                    break;

                default:
                    finalColumnObj.type = column.dataType;
                    break;
            }
            if (column.columnType === "master") {
                finalColumnObj.type = "select";
                finalColumnObj.fieldSettings = {
                    listValues: column.master,
                };
            }

            finalFields[column.name] = finalColumnObj;
        });
        return finalFields;
    },

    generateFunction(funcName: string, type: string, args = 1): any {
        const funcObj: any = {
            label: funcName,
            sqlFunc: funcName,
            returnType: type,
            args: {
                str: {
                    label: type,
                    type,
                    valueSources: ["value", "field", "func"],
                },
            },
            allowSelfNesting: true,
            jsonLogic: funcName,
        };
        if (args) {
            const finalArgs = {};
            for (let index = 0; index < args; index += 1) {
                const element = `${type}${index + 1}`;
                finalArgs[element] = {
                    label: element,
                    type,
                    valueSources: ["value", "field", "func"],
                };
            }

            funcObj.args = finalArgs;
        }
        return funcObj;
    },

};

export default QueryBuilderCustomUtils;